import React, { FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Tooltip,
} from '@material-ui/core';
import { RootState } from '../../store/configureStore';
import * as AnalyticsActions from '../../actions/Analytics/actions';
import {
  MeasurementFilter,
  DEFAULT_BINS,
  AggregateRow,
  CheckTypeName,
  CHECK_TYPE_UNIT,
} from '../../constants/Analytics';
import { UserGenderMap, UserCareLevelMap } from '../../constants/User';

const AXES = [
  { key: 'age_band', label: '年齢帯' },
  { key: 'gender', label: '性別' },
  { key: 'company', label: '法人' },
  { key: 'institution', label: '施設' },
  { key: 'care_level', label: '要介護度' },
];

const axisValueLabel = (axis: string, v: string) => {
  if (axis === 'gender') return UserGenderMap[Number(v)] ?? v;
  if (axis === 'care_level') return UserCareLevelMap[Number(v)] ?? v;
  return v;
};

const DistributionTab: FC<{ filter: MeasurementFilter }> = ({ filter }) => {
  const dispatch = useDispatch();
  const aggregate = useSelector((s: RootState) => s.analytics.aggregate);
  const ct = filter.check_type as CheckTypeName;
  const [binsText, setBinsText] = useState(DEFAULT_BINS[ct].join(','));
  const [axis1, setAxis1] = useState('age_band');
  const [axis2, setAxis2] = useState('');
  const [dedupe, setDedupe] = useState<'latest' | 'all'>('latest');
  const [downloading, setDownloading] = useState(false);

  const parseBins = () =>
    binsText
      .split(',')
      .map(s => Number(s.trim()))
      .filter(n => !Number.isNaN(n));
  const groupBy = () => [axis1, axis2].filter(a => a !== '');

  const run = () => dispatch(AnalyticsActions.aggregateMeasurements(filter, parseBins(), groupBy(), dedupe));

  // 種目が変わったら bins をプリセットに戻し、自動で集計
  useEffect(() => {
    setBinsText(DEFAULT_BINS[ct].join(','));
    dispatch(
      AnalyticsActions.aggregateMeasurements(
        filter,
        DEFAULT_BINS[ct],
        [axis1, axis2].filter(a => a !== ''),
        dedupe,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const axes = aggregate.groupBy;
  const binOrder = Array.from(new Set(aggregate.rows.map(r => r.bin)));
  const rowKeys = Array.from(new Set(aggregate.rows.map(r => axes.map(a => String(r[a])).join(' / '))));
  const cell = (rowKey: string, bin: string): AggregateRow | undefined =>
    aggregate.rows.find(r => axes.map(a => String(r[a])).join(' / ') === rowKey && r.bin === bin);

  const onCsv = async () => {
    setDownloading(true);
    try {
      await AnalyticsActions.downloadAggregateCsv(filter, parseBins(), groupBy(), dedupe);
    } catch {
      alert('CSVダウンロードに失敗しました。');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Box display="flex" alignItems="flex-end" style={{ gap: 16, flexWrap: 'wrap' }}>
          <Tooltip
            arrow
            title={
              <span style={{ fontSize: 13, lineHeight: 1.6 }}>
                測定値を区切って分布を集計するための境界値です。カンマ区切りで指定します。
                <br />
                例: 「8,12,20」→ 8{CHECK_TYPE_UNIT[ct]}未満 / 8〜12{CHECK_TYPE_UNIT[ct]} / 12〜20
                {CHECK_TYPE_UNIT[ct]} / 20{CHECK_TYPE_UNIT[ct]}以上 の4区間に分かれ、分布表の列になります。
              </span>
            }
          >
            <TextField
              label="ビン境界（カンマ区切り）"
              value={binsText}
              onChange={e => setBinsText(e.target.value)}
              style={{ minWidth: 200 }}
              helperText={`単位: ${CHECK_TYPE_UNIT[ct]}（例: ${DEFAULT_BINS[ct].join(',')}）`}
            />
          </Tooltip>
          <FormControl style={{ minWidth: 140 }}>
            <InputLabel>軸1</InputLabel>
            <Select
              value={axis1}
              onChange={e => {
                const v = e.target.value as string;
                setAxis1(v);
                if (v === axis2) setAxis2('');
              }}
            >
              {AXES.map(a => (
                <MenuItem key={a.key} value={a.key}>
                  {a.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl style={{ minWidth: 140 }}>
            <InputLabel>軸2（任意）</InputLabel>
            <Select value={axis2} onChange={e => setAxis2(e.target.value as string)}>
              <MenuItem value="">なし</MenuItem>
              {AXES.filter(a => a.key !== axis1).map(a => (
                <MenuItem key={a.key} value={a.key}>
                  {a.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl style={{ minWidth: 120 }}>
            <InputLabel>集計対象</InputLabel>
            <Select value={dedupe} onChange={e => setDedupe(e.target.value as 'latest' | 'all')}>
              <MenuItem value="latest">最新のみ</MenuItem>
              <MenuItem value="all">全測定</MenuItem>
            </Select>
          </FormControl>
          <Button color="primary" variant="contained" onClick={run} disabled={aggregate.loading}>
            集計
          </Button>
          <Button
            color="primary"
            variant="outlined"
            onClick={onCsv}
            disabled={downloading || aggregate.loading || aggregate.rows.length === 0}
          >
            {downloading ? 'DL中…' : 'CSVダウンロード'}
          </Button>
        </Box>
      </Paper>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        セル = 人数 / 件数
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                {axes.map(a => (AXES.find(x => x.key === a) || { label: a }).label).join(' / ') || '区分'}
              </TableCell>
              {binOrder.map(b => (
                <TableCell key={b} align="right">
                  {b}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowKeys.map(rk => (
              <TableRow key={rk}>
                <TableCell>
                  {rk
                    .split(' / ')
                    .map((v, i) => axisValueLabel(axes[i], v))
                    .join(' / ')}
                </TableCell>
                {binOrder.map(b => {
                  const c = cell(rk, b);
                  return (
                    <TableCell key={b} align="right">
                      {c ? `${c.persons} / ${c.measurements}` : '-'}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DistributionTab;
